<script setup lang="ts">
import { computed, onMounted, ref, watch } from "vue"
import { useI18n } from "vue-i18n"
import { useRoute } from "vue-router"
import { useStore } from "vuex"

import Button from "@/components/shared/Button.vue"
import Card from "@/components/shared/Card.vue"
import CardActions from "@/components/shared/CardActions.vue"
import CardContent from "@/components/shared/CardContent.vue"
import CardTitle from "@/components/shared/CardTitle.vue"
import InputBox from "@/components/shared/InputBox.vue"
import InputSelect from "@/components/shared/InputSelect.vue"
import LayoutAuthed from "@/components/shared/LayoutAuthed.vue"
import Table from "@/components/shared/Table.vue"
import router from "@/plugins/router"
import purchaseService from "@/services/purchase"
import purchaseProductService from "@/services/purchase-product"
import shipmentBundleService from "@/services/shipment-bundle"
import type { Checked } from "@/types/common.type"
import type { Partner } from "@/types/partner.type"
import type { PurchaseProduct } from "@/types/purchase-product.type"
import type { Purchase } from "@/types/purchase.type"
import { reportSentry } from "@/utils/sentry"
import { formattedDate, unitedNumber } from "@/utils/string"

const route = useRoute()
const { t } = useI18n()

const store = useStore()

const partner = computed<Partner | null>(() => store.getters.partner)
const pageTitle = computed(() => {
  return `${t("shipOut.actions.completeShipOut")} > ${t("order.title")} #${order.value?.code || ""}`
})

const order = ref<Purchase | null>(null)
const orderId = computed(() => route.params.order_id)

const products = ref<Checked<PurchaseProduct>[]>([])
const productsToShipOut = computed(() => {
  return products.value.filter((e) => e.checked === true)
})
const firstProductMethod = computed(() => {
  return products.value.filter((e) => e.checked === true)[0]?.deliveryMethod
})

const shipper = ref<string | null>(null)
const trackingCode = ref<string | null>(null)
const trackingLink = ref<string | null>(null)
const shipperOptions = computed(() => [
  { label: "DHL", value: "dhl" },
  { label: "UPS", value: "ups" },
  { label: "Fedex", value: "fedex" },
  { label: "KAS", value: "kas" },
  { label: "Jingdong", value: "jingdong" },
  { label: "SF", value: "sf" },
])

async function loadOrder() {
  const data = await purchaseService.get(Number(orderId.value))
  order.value = data

  order.value.purchaseProducts
    .filter((purchaseProduct) => {
      return purchaseProduct.status === "confirmed" && purchaseProduct.deliveryMethod !== "quick"
    })
    .map((purchaseProduct) => {
      products.value.push({ ...purchaseProduct, checked: false })
    })
}

watch(
  () => trackingCode.value,
  (newVal) => {
    if (newVal) {
      trackingCode.value = newVal.replace(/\s/gi, "")
    }
  },
)

onMounted(async () => {
  try {
    await loadOrder()
  } catch (error) {
    console.error(error)
    reportSentry(error)
    router.replace("/ship_outs")
  }
})

const isSubmitting = ref(false)

async function submitForm() {
  isSubmitting.value = true

  const payload = {
    purchaseProductIds: [] as number[],
    shipper: shipper.value,
    trackingCode: trackingCode.value,
    trackingLink: trackingLink.value,
  }
  productsToShipOut.value.forEach((product) => {
    payload.purchaseProductIds.push(product.id)
  })

  try {
    if (firstProductMethod.value === "logistics") {
      await purchaseProductService.delivery(payload)
    } else if (firstProductMethod.value === "shipping") {
      await shipmentBundleService.create(payload)
    } else {
      return
    }

    store.dispatch("loadPartner", { force: true })

    router.push(`/ship_outs`)
  } catch (err) {
    console.log(err)
    reportSentry(err)
    isSubmitting.value = false
  }
}
</script>

<template>
  <LayoutAuthed :title="pageTitle">
    <form @submit.prevent="submitForm()">
      <div class="row">
        <div class="col-12 col-lg-7 col-xxl-7 mb-4">
          <Card v-if="order">
            <CardTitle>{{ $t("shipOut.actions.chooseProducts") }}</CardTitle>
            <CardContent>
              <Table>
                <template #header>
                  <tr>
                    <th></th>
                    <th>{{ t("item.title") }}</th>
                    <th>{{ t("colorProduct.colorCode") }}</th>
                    <th>{{ t("order.quantity") }}</th>
                    <th>{{ t("order.deliveryMethod") }}</th>
                    <th>{{ t("order.pickupAtDue") }}</th>
                  </tr>
                </template>
                <template #default>
                  <tr v-for="(product, index) in products" :key="index" :class="{ selected: product.checked }">
                    <td>
                      <input
                        type="checkbox"
                        v-model="product.checked"
                        class="form-check-input order-item-check"
                        name="selectedProductIds[]"
                        :value="product.id"
                        :required="productsToShipOut.length === 0"
                        :disabled="!!firstProductMethod && firstProductMethod !== product.deliveryMethod"
                      />
                    </td>
                    <td>{{ product.product.quality.name }}</td>
                    <td>
                      <span v-if="product.salesType === 'standard_order'">{{ product.product.colorCode }}</span>
                      <span v-if="product.salesType === 'print'">{{ t("orderItem.salesTypeList.print") }}</span>
                    </td>
                    <td>{{ unitedNumber(product.quantity, t("item.units.yards_short")) }}</td>
                    <td>{{ t(`order.deliveryMethodList.${product.deliveryMethod}`) }}</td>
                    <td>{{ formattedDate(product.pickupAt) }}</td>
                  </tr>
                </template>
              </Table>
            </CardContent>
          </Card>
        </div>
        <div class="col-12 col-lg-5 col-xxl-5 mb-4">
          <Card v-if="partner && order">
            <CardTitle>{{ $t("shipOut.actions.inputDetails") }}</CardTitle>
            <CardContent>
              <InputBox
                v-if="partner.supplierType !== 'independent'"
                type="text"
                v-model="shipper"
                :label="$t('shipOut.shipper')"
                field="shipper"
                required
              />
              <InputSelect
                v-else
                type="text"
                v-model="shipper"
                :label="$t('shipOut.shipper')"
                field="shipper"
                :options="shipperOptions"
                required
              />
              <InputBox type="text" v-model="trackingCode" :label="$t('shipOut.trackingCode')" field="trackingCode" />
              <InputBox type="url" v-model="trackingLink" :label="$t('shipOut.trackingLink')" field="trackingLink" />
            </CardContent>
            <CardActions justifyEnd>
              <Button type="submit" :text="$t('shared.save')" :isSubmitting="isSubmitting" />
            </CardActions>
          </Card>
        </div>
      </div>
    </form>
  </LayoutAuthed>
</template>

<style scoped>
tr.selected {
  td {
    background-color: var(--bs-success-bg-subtle);
  }
}
</style>
